import { VideoTypeEnum } from "../../types";
import { RecordGroup } from "./useBatchUpload";

interface GroupItemProps {
    group: RecordGroup;
    originalIndex: number;
    onReuploadChange?: (index: number, value: boolean) => void;
}

const GroupItem = ({ group, originalIndex, onReuploadChange }: GroupItemProps) => {
    const displayName = (group.hashStatus !== 'new' && group.existingRecordName)
        ? group.existingRecordName
        : group.recordName;

    return (
        <li className="list-group-item">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>{displayName}</strong>
                <div className="d-flex align-items-center gap-3">
                    {group.hashStatus === 'reuploading' && onReuploadChange && (
                        <div className="form-check mb-0">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id={`reupload-${originalIndex}`}
                                checked={group.reupload}
                                onChange={e => onReuploadChange(originalIndex, e.target.checked)}
                            />
                            <label className="form-check-label small" htmlFor={`reupload-${originalIndex}`}>
                                Reupload
                            </label>
                        </div>
                    )}
                    <span className={`badge ${group.type === VideoTypeEnum.PANORAMA ? 'bg-success' : 'bg-primary'}`}>
                        {group.type}
                    </span>
                </div>
            </div>
            <div>
                <small className="text-muted">
                    Date: {group.lastModifiedDate.toLocaleString()}
                </small>
            </div>
            <div>
                <small>
                    {group.videos.length} video{group.videos.length !== 1 ? 's' : ''}
                </small>
            </div>
            <div className="mt-2">
                <ul className="list-unstyled ms-3 small">
                    {group.videos.slice(0, 3).map((video, vidIndex) => (
                        <li key={vidIndex}>{video.file.name}</li>
                    ))}
                    {group.videos.length > 3 && (
                        <li>...and {group.videos.length - 3} more</li>
                    )}
                </ul>
            </div>
        </li>
    );
};

interface SectionProps {
    title: string;
    variant: 'primary' | 'warning' | 'success';
    items: Array<{ group: RecordGroup; originalIndex: number }>;
    onReuploadChange?: (index: number, value: boolean) => void;
}

const Section = ({ title, variant, items, onReuploadChange }: SectionProps) => {
    if (items.length === 0) return null;
    return (
        <div className="mb-3">
            <ul className="list-group">
                <li className={`list-group-item list-group-item-${variant} fw-semibold`}>
                    {title} ({items.length})
                </li>
                {items.map(({ group, originalIndex }) => (
                    <GroupItem
                        key={originalIndex}
                        group={group}
                        originalIndex={originalIndex}
                        onReuploadChange={onReuploadChange}
                    />
                ))}
            </ul>
        </div>
    );
};

interface RecordListProps {
    records: RecordGroup[];
    onReuploadChange?: (index: number, value: boolean) => void;
}

export const RecordList = ({ records, onReuploadChange }: RecordListProps) => {
    const indexed = records.map((group, i) => ({ group, originalIndex: i }));
    const newItems = indexed.filter(({ group }) => group.hashStatus === 'new');
    const reuploadingItems = indexed.filter(({ group }) => group.hashStatus === 'reuploading');
    const alreadyLoadedItems = indexed.filter(({ group }) => group.hashStatus === 'already_loaded');

    return (
        <div className="mb-3">
            <p>The following records will be created:</p>
            <Section
                title="New videos"
                variant="primary"
                items={newItems}
            />
            <Section
                title="Reuploading — already exists but not fully uploaded"
                variant="warning"
                items={reuploadingItems}
                onReuploadChange={onReuploadChange}
            />
            <Section
                title="Already loaded — upload will be skipped"
                variant="success"
                items={alreadyLoadedItems}
            />
        </div>
    );
};
